import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { bold, num, tableRows } from "../../utils/xlsx";
import {
  ACCOUNTING,
  cellRight,
  defaultPeriod,
  download,
  EmptyState,
  ExportButton,
  LoadingOverlay,
  money,
  PeriodFilter,
  periodLabel,
  REPORTS,
  sheetHeading,
  validPeriod,
} from "./common";
import LedgerVouchers from "./LedgerVouchers";

const AMOUNT_COLUMNS = [
  { label: "Opening Dr", key: "openingDebit" },
  { label: "Opening Cr", key: "openingCredit" },
  { label: "Debit", key: "debit" },
  { label: "Credit", key: "credit" },
  { label: "Closing Dr", key: "closingDebit" },
  { label: "Closing Cr", key: "closingCredit" },
];

const blank = (value) => (Number(value) ? money(value) : "");

/** Ledger-wise opening, debit, credit and closing totals for a period (trial balance), with group subtotals and drill-down. */
const LedgerWiseTotals = () => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [applied, setApplied] = useState(null);
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState("");
  const [includeZero, setIncludeZero] = useState(false);
  const [view, setView] = useState("ledger");
  const [search, setSearch] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    api.get(`${ACCOUNTING}/groups`).then((data) => setGroups(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const load = async () => {
    if (!validPeriod(period)) return;
    setLoading(true);
    try {
      setReport(
        await api.get(`${REPORTS}/ledger-wise`, { fromDate: period.fromDate, toDate: period.toDate, group: group || undefined, includeZero })
      );
      setApplied(period);
    } catch (error) {
      toast.error(error.message || "Failed to load ledger-wise totals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ledgerRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (report?.ledgers || []).filter((row) => !needle || row.ledgerName.toLowerCase().includes(needle) || row.groupLabel.toLowerCase().includes(needle));
  }, [report, search]);

  const totalsOf = (rows) => AMOUNT_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: rows.reduce((sum, row) => sum + Number(row[c.key] || 0), 0) }), {});
  const rows = view === "ledger" ? ledgerRows : report?.groups || [];
  const totals = totalsOf(rows);

  const exportExcel = () => {
    const amountColumns = AMOUNT_COLUMNS.map((c) => ({ ...c, format: "money" }));
    const heading = sheetHeading("Ledger-wise Totals (Trial Balance)", periodLabel(applied));
    download(`Ledger wise totals ${periodLabel(applied)}`, [
      {
        name: "Ledger-wise",
        rows: [
          ...heading,
          ...tableRows(
            [{ label: "Ledger", key: "ledgerName" }, { label: "Group", key: "groupLabel" }, { label: "Primary Group", key: "primaryGroupLabel" }, ...amountColumns],
            ledgerRows,
            { totals: true }
          ),
          [],
          [bold("Opening Stock"), num(report.openingStock)],
          [bold("Closing Stock"), num(report.closingStock)],
          [report.note],
        ],
        widths: [36, 24, 22, 15, 15, 15, 15, 15, 15],
      },
      {
        name: "Group-wise",
        rows: [
          ...heading,
          ...tableRows(
            [{ label: "Group", key: "groupLabel" }, { label: "Primary Group", key: "primaryGroupLabel" }, { label: "Ledgers", key: "ledgerCount", format: "int" }, ...amountColumns],
            report.groups,
            { totals: true }
          ),
        ],
        widths: [28, 22, 9, 15, 15, 15, 15, 15, 15],
      },
    ]);
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Total amount per ledger for the period: opening balance, debits, credits and closing balance. Income and expense ledgers restart every 1
        April. Click a ledger to see its vouchers.
      </div>
      <div className="filters-bar">
        <PeriodFilter period={period} onChange={setPeriod} />
        <select value={group} onChange={(e) => setGroup(e.target.value)} aria-label="Account group">
          <option value="">All groups</option>
          {groups
            .filter((g) => g.selectable)
            .map((g) => (
              <option key={g.code} value={g.code}>
                {g.parent ? "— " : ""}
                {g.label}
              </option>
            ))}
        </select>
        <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
          <input type="checkbox" checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)} />
          Show zero-balance ledgers
        </label>
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!report} />
      </div>

      {!report ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          <div className="filters-bar" style={{ marginTop: "10px" }}>
            <button className={view === "ledger" ? "primary" : "secondary"} onClick={() => setView("ledger")}>
              Ledger-wise
            </button>
            <button className={view === "group" ? "primary" : "secondary"} onClick={() => setView("group")}>
              Group-wise
            </button>
            {view === "ledger" ? <input type="text" placeholder="Search ledger or group" value={search} onChange={(e) => setSearch(e.target.value)} /> : null}
            <span className="page-subtitle">
              Opening stock ₹{money(report.openingStock)} · Closing stock ₹{money(report.closingStock)}
            </span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>{view === "ledger" ? "Ledger" : "Group"}</th>
                  <th style={{ textAlign: "left" }}>{view === "ledger" ? "Group" : "Primary Group"}</th>
                  {AMOUNT_COLUMNS.map((c) => (
                    <th key={c.key} style={cellRight}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState>No ledgers with activity in this period</EmptyState>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={view === "ledger" ? row.ledgerId : row.group}>
                      <td style={{ textAlign: "left" }}>
                        {view === "ledger" ? (
                          <button
                            type="button"
                            onClick={() => setDrill(row)}
                            style={{ background: "none", border: "none", padding: 0, color: "var(--color-primary)", cursor: "pointer", textAlign: "left" }}
                          >
                            {row.ledgerName}
                          </button>
                        ) : (
                          `${row.groupLabel} (${row.ledgerCount})`
                        )}
                      </td>
                      <td style={{ textAlign: "left" }}>{view === "ledger" ? row.groupLabel : row.primaryGroupLabel}</td>
                      {AMOUNT_COLUMNS.map((c) => (
                        <td key={c.key} style={cellRight}>
                          {blank(row[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {rows.length ? (
                <tfoot>
                  <tr>
                    <td colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>
                      Total
                    </td>
                    {AMOUNT_COLUMNS.map((c) => (
                      <td key={c.key} style={{ ...cellRight, fontWeight: 700 }}>
                        {money(totals[c.key])}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </>
      )}

      {drill ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setDrill(null)}>
          <div className="modal-box" style={{ maxWidth: "1100px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Ledger Vouchers — {drill.ledgerName}</h3>
              <button className="close-btn" onClick={() => setDrill(null)}>
                Close
              </button>
            </div>
            <LedgerVouchers initialLedgerId={drill.ledgerId} initialPeriod={applied} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LedgerWiseTotals;
